import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";

import useFetch from "../../../components/shared/useFetch";

const UpdateBankBalanceModal = ({
  selectedMonth,
  financial_year,
  setActiveBanks,
  setShowModal,
}) => {
  const [{ user }] = useAtom(gbState);

  const [bankList, setBankList] = useState([]);
  const [bankListIDs, setBankListIDs] = useState([]);
  const [bankBalance, setBankBalance] = useState([]);
  const [balanceInputs, setBalanceInputs] = useState({});

  // useFetch for refetching bank balances after update
  const {
    data: bankBalanceData,
    loading: bankBalanceLoading,
    error: bankBalanceError,
    refetch: bankBalanceRefetch,
  } = useFetch(
    `${process.env.REACT_APP_API_URL}/bank-balance/church/${user?.church_id}`
  );

  //Get all the Activate banks ID from bank list
  useEffect(() => {
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/banks/${user.church_id}`;
    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then((response) => {
        //filter for all active_status = true
        const activeBanks = response.data.banks.filter(
          (bank) => bank.active_status === true
        );
        setBankList(activeBanks);
        setBankListIDs(activeBanks.map((bank) => bank._id));
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  //Get all the balances for the selected month using API: /bank/:bank_id/:month
  //using useEffect iterate through bankListIDs and get the balances for the selected month
  useEffect(() => {
    let userToken = localStorage.getItem("token");
    const fetchBalances = async () => {
      const balances = [];
      for (const bankID of bankListIDs) {
        const urlLink = `${process.env.REACT_APP_API_URL}/bank-balance/bank/${bankID}/${selectedMonth?.value}/${financial_year?.value}`;
        const config = {
          method: "GET",
          url: urlLink,
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        };
        try {
          const response = await axios(config);
          balances.push(response.data);
        } catch (error) {
          console.log(error);
        }
      }
      setBankBalance(balances);
    };
    if (bankListIDs.length > 0 && selectedMonth && financial_year) {
      fetchBalances();
    }
  }, [bankListIDs, selectedMonth, financial_year]);

  // Initialize balance inputs when data is loaded
  useEffect(() => {
    if (bankList.length > 0 && bankBalance.length > 0) {
      const initialInputs = {};

      bankList.forEach((bank) => {
        // Find the corresponding balance data for this bank
        const bankBalanceData = bankBalance.find((balanceItem) => {
          return (
            balanceItem.bankBalances &&
            balanceItem.bankBalances.some(
              (balance) => balance.bank_id === bank._id
            )
          );
        });

        const balanceInfo = bankBalanceData?.bankBalances?.find(
          (balance) => balance.bank_id === bank._id
        );

        initialInputs[bank._id] = {
          record_id: balanceInfo?._id || null,
          opening_balance: balanceInfo?.opening_balance || "",
          closing_balance: balanceInfo?.closing_balance || "",
        };
      });

      setBalanceInputs(initialInputs);
    }
  }, [bankList, bankBalance]);

  const handleBalanceChange = (bankId, type, value) => {
    setBalanceInputs((prev) => ({
      ...prev,
      [bankId]: {
        ...prev[bankId],
        [type]: value,
      },
    }));
  };

  const handleUpdate = () => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "POST",
      url: `${process.env.REACT_APP_API_URL}/bank-balance/update-balance/`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: {
        balances: balanceInputs,
        month: selectedMonth.value,
        financial_year: financial_year.value,
      },
    };

    axios(config)
      .then((response) => {
        toast.success("Bank balances updated successfully");

        // Refetch bank balance data directly
        const refetchConfig = {
          method: "GET",
          url: `${process.env.REACT_APP_API_URL}/bank-balance/church/${user?.church_id}`,
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        };

        axios(refetchConfig)
          .then((refetchResponse) => {
            if (refetchResponse.data) {
              setActiveBanks(
                refetchResponse.data?.bankBalances?.filter((b) => {
                  return b?.bank_id.active_status === true;
                })
              );
            }
          })
          .catch((refetchError) => {
            console.error("Error refetching data:", refetchError);
          });

        setShowModal(false);
      })
      .catch((error) => {
        toast.error("Failed to update bank balances");
        console.error(error);
      });
  };

  return (
    <div className="max-w-4xl p-6 mx-auto bg-white ">
      {/* Header Information */}
      <div className="mb-6">
        <h2 className="mb-4 text-2xl font-bold text-gray-800">
          Update Bank Balances
        </h2>

        <div className="p-4 mb-4 rounded-lg bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Financial Year
              </label>
              <div className="text-lg font-semibold text-blue-600">
                {financial_year?.label || "Not Selected"}
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Month
              </label>
              <div className="text-lg font-semibold text-blue-600">
                {selectedMonth?.label || "Not Selected"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Balance Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">
                Bank Name
              </th>
              <th className="w-32 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">
                Account Number
              </th>
              <th className="w-40 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">
                Opening Balance
              </th>
              <th className="w-40 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b">
                Closing Balance
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {bankList.map((bank, index) => (
              <tr
                key={bank._id}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {bank.bank_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {bank.branch && `Branch: ${bank.branch}`}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                  {bank.account_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="number"
                    className="w-full min-w-[160px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Opening balance"
                    value={balanceInputs[bank._id]?.opening_balance || ""}
                    onChange={(e) =>
                      handleBalanceChange(
                        bank._id,
                        "opening_balance",
                        e.target.value
                      )
                    }
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="number"
                    className="w-full min-w-[160px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Closing balance"
                    value={balanceInputs[bank._id]?.closing_balance || ""}
                    onChange={(e) =>
                      handleBalanceChange(
                        bank._id,
                        "closing_balance",
                        e.target.value
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Update Button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handleUpdate}
          className="px-6 py-3 font-medium text-white transition-colors duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Update Bank Balances
        </button>
      </div>

      {/* Loading State */}
      {bankList.length === 0 && (
        <div className="py-8 text-center">
          <div className="inline-block w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
          <p className="mt-2 text-gray-600">Loading bank data...</p>
        </div>
      )}

      {/* {JSON.stringify(bankBalance)} */}
    </div>
  );
};

export default UpdateBankBalanceModal;
